import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Alert, AlertDescription } from './ui/alert';
import { Loader2, MessageSquare, User, Lock, AlertCircle } from 'lucide-react';
import { useAppData } from '../hooks/useAppData';
import { Usuario } from '../hooks/useUsuarios';

interface PublicChatLoginProps {
  onLoginSuccess: (usuario: Usuario) => void;
}

export default function PublicChatLogin({ onLoginSuccess }: PublicChatLoginProps) {
  const { usuarios } = useAppData();
  const [nombre, setNombre] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [intentosFallidos, setIntentosFallidos] = useState(0);
  const [bloqueadoHasta, setBloqueadoHasta] = useState<Date | null>(null);

  // Verificar si está bloqueado
  const estaBloqueado = Boolean(bloqueadoHasta && new Date() < bloqueadoHasta);

  // Limpiar bloqueo cuando expire
  useEffect(() => {
    if (bloqueadoHasta && new Date() >= bloqueadoHasta) {
      setBloqueadoHasta(null);
      setIntentosFallidos(0);
      setError('');
    }
  }, [bloqueadoHasta]);

  // Timer para mostrar tiempo restante de bloqueo
  const [tiempoRestante, setTiempoRestante] = useState(0);
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (estaBloqueado && bloqueadoHasta) {
      interval = setInterval(() => {
        const ahora = new Date();
        const restante = Math.max(0, Math.ceil((bloqueadoHasta.getTime() - ahora.getTime()) / 1000));
        setTiempoRestante(restante);
        if (restante === 0) {
          setBloqueadoHasta(null);
          setIntentosFallidos(0);
          setError('');
        }
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [estaBloqueado, bloqueadoHasta]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (estaBloqueado) {
      setError(`Acceso bloqueado. Intenta en ${tiempoRestante} segundos.`);
      return;
    }

    if (!nombre.trim() || !password.trim()) {
      setError('Por favor ingresa tu nombre de usuario y contraseña');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Buscar socio por nombre y password
      const socio = usuarios.find((u: Usuario) =>
        u.tipo === 'socio' &&
        u.nombre.toLowerCase().trim() === nombre.toLowerCase().trim() &&
        u.password === password &&
        u.activo &&
        !u.bloqueado
      );

      if (socio) {
        // Login exitoso
        setIntentosFallidos(0);
        setBloqueadoHasta(null);
        onLoginSuccess(socio);
      } else {
        // Login fallido
        const nuevosIntentos = intentosFallidos + 1;
        setIntentosFallidos(nuevosIntentos);

        if (nuevosIntentos >= 5) {
          // Bloquear por 5 minutos después de 5 intentos fallidos
          const tiempoBloqueo = new Date();
          tiempoBloqueo.setMinutes(tiempoBloqueo.getMinutes() + 5);
          setBloqueadoHasta(tiempoBloqueo);
          setError('Demasiados intentos fallidos. Acceso bloqueado por 5 minutos.');
        } else {
          setError(`Credenciales incorrectas. Intentos restantes: ${5 - nuevosIntentos}`);
        }

        setPassword('');
      }
    } catch (err) {
      console.error('Error en login:', err);
      setError('Error de conexión. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  const formatTiempo = (segundos: number) => {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${minutos}:${segs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white shadow-xl border-0">
        <CardHeader className="text-center space-y-2 pb-4">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <MessageSquare className="h-8 w-8 text-purple-600" />
            <CardTitle className="text-2xl font-bold text-gray-900">
              Asistente Bimobi
            </CardTitle>
          </div>
          <CardDescription className="text-gray-600">
            Ingresa con tu usuario de socio para acceder al chat
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-red-800">
                {error}
                {estaBloqueado && (
                  <div className="mt-1 font-mono text-sm">
                    Tiempo restante: {formatTiempo(tiempoRestante)}
                  </div>
                )}
              </AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="nombre" className="text-sm font-medium text-gray-700">
                <User className="inline h-4 w-4 mr-1" />
                Nombre de Usuario
              </Label>
              <Input
                id="nombre"
                type="text"
                placeholder="Ingresa tu nombre de usuario"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                disabled={loading || estaBloqueado}
                className="h-12 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                autoComplete="username"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                <Lock className="inline h-4 w-4 mr-1" />
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="Ingresa tu contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading || estaBloqueado}
                className="h-12 border-gray-300 focus:border-purple-500 focus:ring-purple-500"
                autoComplete="current-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading || estaBloqueado || !nombre.trim() || !password.trim()}
              className="w-full h-12 bg-purple-600 hover:bg-purple-700 text-white font-medium"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Verificando...
                </>
              ) : estaBloqueado ? (
                `Bloqueado (${formatTiempo(tiempoRestante)})`
              ) : (
                'Ingresar al Chat'
              )}
            </Button>
          </form>

          <div className="text-center">
            <div className="text-xs text-gray-500 space-y-1">
              <p>Solo usuarios tipo "Socio" pueden acceder</p>
              <p>Contacta al administrador si necesitas ayuda</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
